import { colors } from '@kmns-internal/furikake/tokens'
import { LocationOn } from '@mui/icons-material'
import { Autocomplete, Chip, Stack, TextField, Typography } from '@mui/material'
import {
  PlaceNodeTypeEnum,
  type PlaceNode,
} from '@ulysses-inc/harami_api_client'
import { useId, type FC, type SyntheticEvent } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import { updatePlaceNodes } from 'src/state/ducks/templates/editTemplate/actions'
import type { RootState } from 'src/state/store'

const PlaceRow: FC = () => {
  const dispatch = useDispatch()

  const templatePlaceNodes = useSelector(
    (state: RootState) => state.placesState.placeNodes.nodes,
    shallowEqual,
  )
  const selectedPlaceNodes = useSelector(
    (state: RootState) => state.templatesState.editTemplate.selectedPlaceNodes,
    shallowEqual,
  )

  const onChange = (_: SyntheticEvent, value: PlaceNode[]) =>
    dispatch(updatePlaceNodes(value.map(({ uuid }) => uuid ?? '')))

  // FIXME `?` いらないはずだが元コードに存在し怖いので調査するまでつけておく
  const selectedUuids = selectedPlaceNodes?.map(({ uuid }) => uuid) ?? []
  const selectedValue = (templatePlaceNodes ?? []).filter(({ uuid }) =>
    selectedUuids.includes(uuid ?? ''),
  )

  const labelId = useId()

  return (
    <Stack spacing={1} sx={{ mx: 2 }}>
      <Typography variant="subtitle1" id={labelId}>
        現場の設定
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={templatePlaceNodes ?? []}
        getOptionLabel={option =>
          option.type === PlaceNodeTypeEnum.PlaceGroup
            ? (option.placeGroup?.name ?? '')
            : (option.place?.name ?? '')
        }
        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
        value={selectedValue}
        onChange={onChange}
        renderValue={(tagValue, getItemProps) =>
          tagValue.map((option, index) => {
            const { key, ...chipProps } = getItemProps({ index })
            const label =
              option.type === PlaceNodeTypeEnum.PlaceGroup
                ? option.placeGroup?.name
                : (option.place?.name ?? '')
            return (
              <Chip
                key={key}
                {...chipProps}
                size="small"
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {option.type === PlaceNodeTypeEnum.PlaceGroup ? (
                      <PlaceGroupSVG
                        style={{ width: 14, color: colors.action.active }}
                      />
                    ) : (
                      <LocationOn
                        sx={{ fontSize: 14, color: colors.action.active }}
                      />
                    )}
                    <span>{label}</span>
                  </Stack>
                }
                variant="pale"
              />
            )
          })
        }
        renderOption={(props, option) => (
          <li {...props} key={option.uuid}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {option.type === PlaceNodeTypeEnum.PlaceGroup ? (
                <PlaceGroupSVG
                  style={{ width: 16, color: colors.action.active }}
                />
              ) : (
                <LocationOn
                  fontSize="small"
                  sx={{ width: 16, height: 16, color: colors.action.active }}
                />
              )}
              <span>
                {option.type === PlaceNodeTypeEnum.PlaceGroup
                  ? option.placeGroup?.name
                  : (option.place?.name ?? '')}
              </span>
            </Stack>
          </li>
        )}
        renderInput={({ inputProps: paramsInputProps, ...params }) => (
          <TextField
            {...params}
            size="small"
            slotProps={{
              htmlInput: {
                ...paramsInputProps,
                'aria-labelledby': labelId,
                'data-testid': 'place-select',
              },
            }}
          />
        )}
      />
    </Stack>
  )
}

export default PlaceRow
